/**
 * main.ts — Vite entry point for the Suite Sofistic SPA.
 *
 * Boot order matters: the router's first navigation guard reads session claims
 * SYNCHRONOUSLY. Since the JWT now lives in an httpOnly cookie (not decodable by
 * JS), we must hydrate the claims from GET /auth/session BEFORE mounting, so the
 * first guard sees the real auth state instead of bouncing a logged-in user to
 * /login on every refresh. hydrateSession() never throws (401 → unauthenticated).
 */
import { createApp } from 'vue';
import App from './App.vue';
import { router } from './router/index.js';
import { hydrateSession, startSessionRefreshTimer, startIdleTracking } from './lib/session.js';
import { useTheme } from './composables/useTheme.js';
import './style.css';

// Apply the persisted/system theme to <html> BEFORE mounting, unconditionally —
// not every route mounts AppTopbar (e.g. public/dev-only routes), so relying on
// a component importing useTheme.ts to trigger its module-load side effect would
// silently skip dark mode on those pages. Called here so it always runs.
useTheme();

// The browser back/forward button can restore a fully-hydrated SPA snapshot
// from bfcache WITHOUT re-running this file — including a stale tenant/company
// context if the session cookie changed since the snapshot was taken (e.g. the
// user switched company, then hit Back). A stale snapshot showing company A's
// data while API calls authenticate as company B produces confusing 404s on
// writes (RLS correctly rejects them, but the UI never told the user why).
// Force a real reload so every navigation re-hydrates from the CURRENT session.
//
// Guarded with sessionStorage (not a bare `if (event.persisted)`, fixed
// 2026-08-24): some browsers' tab-sleeping/discard-and-restore (observed in
// Edge) can re-fire `pageshow` with `persisted: true` again right after the
// reload this triggers, which without a guard becomes an unbounded
// reload loop — the tab never finishes navigating, so it never responds to
// close/DevTools either. Same one-shot pattern as the `vite:preloadError`
// guard below; cleared on mount so the guard is live again next time.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  const key = 'suite:bfcache-reload';
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  window.location.reload();
});

// A tab left open across a deploy still runs the PREVIOUS build's entry
// bundle in memory. Clicking into a route whose view is a lazy chunk (e.g.
// ServiceOrdersView.vue) makes it import() a hashed filename that no longer
// exists — Cloudflare Pages only serves the LATEST deploy's assets, so that
// request 404s, `_redirects`' SPA catch-all (`/* -> /index.html 200`) serves
// index.html instead, and the browser refuses to run text/html as a module
// (this is Vite's `vite:preloadError` event: sidebar/topbar clicks silently
// do nothing). A reload fetches the current index.html + current chunk
// hashes and fixes it. sessionStorage (not a module-level flag) survives the
// reload itself, so a genuine outage doesn't loop forever; it's cleared once
// the app mounts so the guard is live again for the NEXT deploy.
//
// The guard is a timestamp, not a flag cleared on mount (fixed 2026-10-13):
// while a deploy propagates, an edge can serve the new index.html with chunks
// that still 404. The reload then mounts fine, the cleared flag re-arms, the
// next click fails again and every navigation became a reload. One reload per
// 30s lets a real deploy self-heal without ever looping.
/** One reload per 30s (see above). Returns false when the guard is still cooling down. */
function reloadAfterStaleChunk(target?: string): boolean {
  const key = 'suite:preload-error-reload';
  const last = Number(sessionStorage.getItem(key) ?? 0);
  if (Date.now() - last < 30_000) return false;
  sessionStorage.setItem(key, String(Date.now()));
  if (target) window.location.assign(target);
  else window.location.reload();
  return true;
}

window.addEventListener('vite:preloadError', (event) => {
  if (reloadAfterStaleChunk()) event.preventDefault();
});

// A route's lazy view whose chunk is gone fails as a plain import() rejection,
// which reaches the router — not `vite:preloadError` — and left the page blank
// (reported 2026-10-14 after a day of deploys). Same guarded reload, landing on
// the route the user was going to.
router.onError((error: unknown, to) => {
  const message = error instanceof Error ? error.message : String(error);
  if (/dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message)) {
    reloadAfterStaleChunk(to.fullPath);
  }
});

void hydrateSession().finally(() => {
  createApp(App).use(router).mount('#app');
  sessionStorage.removeItem('suite:bfcache-reload');
  // Session bug fix (2026-07-21): keep an active session alive silently instead
  // of cutting the user off mid-work with no warning. No-ops until authenticated.
  startSessionRefreshTimer();
  // Idle timeout (2026-07-25): the refresh above keeps an active session alive
  // forever — this separately checks whether anyone is actually there.
  startIdleTracking();
});
